import React from 'react';
import { Heart, Star, ShoppingBag, Eye } from 'lucide-react';
import { useLojaCart } from '../../context/LojaCartContext';

export default function ProductCard({ product, onOpenQuickView }) {
  const { toggleFavorite, isFavorite } = useLojaCart();
  const isFav = isFavorite(product.id);

  const price = product.price;
  const promoPrice = product.promotional_price;
  const hasDiscount = promoPrice && promoPrice < price;
  const discountPercent = hasDiscount 
    ? Math.round(((price - promoPrice) / price) * 100) 
    : 0;

  const primaryImage = (product.images && product.images.length > 0)
    ? (product.images.find(i => i.is_primary)?.url || product.images[0].url || product.images[0])
    : '/images/produtos/camisa-urb.png';

  // Cores distintas presentes nas variantes
  const uniqueColors = Array.from(
    new Set((product.variants || []).map(v => v.color_hex || '#0d120f'))
  ).slice(0, 4);

  return (
    <div className="group relative rounded-2xl bg-urb-card border border-urb-border hover:border-urb-primary/40 transition-all duration-300 flex flex-col overflow-hidden hover:shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
      
      {/* Imagem & Badges */}
      <div className="relative aspect-square w-full bg-gradient-to-b from-urb-card-light/50 to-urb-dark/80 p-6 flex items-center justify-center overflow-hidden cursor-pointer"
        onClick={() => onOpenQuickView(product)}
      >
        {/* Badges de Destaque */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10">
          {hasDiscount && (
            <span className="px-2.5 py-1 rounded-md bg-urb-primary text-black font-black text-[10px] uppercase tracking-wider shadow-md">
              -{discountPercent}% OFF
            </span>
          )}
          {product.featured ? (
            <span className="px-2.5 py-1 rounded-md bg-urb-dark/80 backdrop-blur-md border border-urb-primary/30 text-urb-primary font-bold text-[10px] uppercase tracking-wider">
              DROP OFICIAL
            </span>
          ) : null}
        </div>

        {/* Botão de Favorito */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite(product.id);
          }}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-md transition-all z-10 cursor-pointer ${
            isFav
              ? 'bg-red-500/20 text-red-400 border border-red-500/40 shadow-[0_0_12px_rgba(239,68,68,0.3)]'
              : 'bg-urb-dark/70 text-urb-text-muted hover:text-white border border-urb-border hover:border-urb-primary/30'
          }`}
          aria-label={isFav ? "Remover dos favoritos" : "Salvar nos favoritos"}
        >
          <Heart className={`w-4 h-4 ${isFav ? 'fill-red-400' : ''}`} />
        </button>

        {/* Imagem do Produto */}
        <img
          src={primaryImage}
          alt={product.name}
          className="w-full h-full object-contain drop-shadow-[0_12px_20px_rgba(0,0,0,0.7)] group-hover:scale-108 transition-transform duration-500 ease-out"
          onError={(e) => {
            if (!e.target.src.includes('/loja/')) {
              e.target.src = `/loja${primaryImage}`;
            }
          }}
        />

        {/* Quick View Hover Pill */}
        <div className="absolute inset-x-4 bottom-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-10 pointer-events-none">
          <div className="px-4 py-2 rounded-xl bg-urb-dark/90 backdrop-blur-md border border-urb-primary/40 text-urb-primary text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 shadow-xl">
            <Eye className="w-3.5 h-3.5" />
            <span>Ver Detalhes</span>
          </div>
        </div>
      </div>

      {/* Conteúdo do Card */}
      <div className="p-4 sm:p-5 flex flex-col flex-1 justify-between gap-4">
        
        <div className="space-y-1.5">
          {/* Metadados: Gênero e Avaliações */}
          <div className="flex items-center justify-between text-[11px] text-urb-text-muted">
            <span className="font-semibold uppercase tracking-wider text-urb-primary/80">
              {product.gender || 'Unissex'}
            </span>
            <div className="flex items-center gap-1 text-yellow-400 font-bold">
              <Star className="w-3 h-3 fill-yellow-400" />
              <span>{product.rating_avg || '4.9'}</span>
              <span className="text-urb-text-muted">({product.rating_count || 12})</span>
            </div>
          </div>

          {/* Nome do Produto */}
          <h3
            onClick={() => onOpenQuickView(product)}
            className="text-sm sm:text-base font-bold text-white group-hover:text-urb-primary transition-colors line-clamp-2 cursor-pointer leading-snug"
          >
            {product.name}
          </h3>

          {/* Breve Descrição */}
          <p className="text-xs text-urb-text-secondary line-clamp-2 leading-relaxed">
            {product.short_description}
          </p>
        </div>

        {/* Rodapé: Swatches, Preço e Botão */}
        <div className="space-y-3 pt-2 border-t border-urb-border/50">
          
          <div className="flex items-center justify-between">
            {/* Swatches de Cores */}
            <div className="flex items-center gap-1.5">
              {uniqueColors.map((hex, i) => (
                <span
                  key={i}
                  className="w-2.5 h-2.5 rounded-full border border-white/20"
                  style={{ backgroundColor: hex }}
                />
              ))}
            </div>

            {/* Preços */}
            <div className="text-right">
              {hasDiscount && (
                <span className="text-[11px] text-urb-text-muted line-through block leading-none mb-0.5">
                  R$ {price.toFixed(2).replace('.', ',')}
                </span>
              )}
              <span className="text-base sm:text-lg font-black text-white text-gradient">
                R$ {(promoPrice || price).toFixed(2).replace('.', ',')}
              </span>
            </div>
          </div>

          {/* Botão de Ação */}
          <button
            type="button"
            onClick={() => onOpenQuickView(product)}
            className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-urb-card-light hover:bg-urb-primary hover:text-black text-white font-bold text-xs uppercase tracking-wider border border-urb-border hover:border-urb-primary transition-all duration-200 cursor-pointer group/btn"
          >
            <ShoppingBag className="w-3.5 h-3.5 group-hover/btn:scale-110 transition-transform" />
            <span>ESCOLHER & COMPRAR</span>
          </button>

        </div>

      </div>

    </div>
  );
}
